import React, { useState, useEffect, useCallback } from 'react'
import { CalendarOutlined, LaptopOutlined, BookOutlined, BulbOutlined, GiftOutlined } from '@ant-design/icons'
import { message } from 'antd'
import './App.css'

// 类型和组件导入
import type { SkillKey, SkillCard, HistoryItem, Message, ApiMessage } from './types'
import { useChat, useHistory } from './hooks'
import { Sidebar, MessageList, InputArea, RightPanel } from './components'
import { getUserId } from './utils/userId'
import { SKILL_DATA, WELCOME_TEXT, TYPEWRITER_CONFIG } from './config'

// 技能卡片配置
const skillCards: SkillCard[] = [
  { key: 'activity', title: '启航杯活动安排', icon: <CalendarOutlined />, color: 'blue' },
  { key: 'system', title: '启航杯系统操作', icon: <LaptopOutlined />, color: 'purple' },
  { key: 'learning', title: '启航杯历届作品学习', icon: <BookOutlined />, color: 'peach' },
  { key: 'growth', title: '成长锦囊对对碰', icon: <GiftOutlined />, color: 'cyan' }
]

// 推荐问题组件
interface SuggestionListProps {
  skill: SkillKey
  onSuggestionClick: (question: string) => void
}

const SuggestionList: React.FC<SuggestionListProps> = ({ skill, onSuggestionClick }) => (
  <>
    <div className="suggestion-title">
      <BulbOutlined /> 猜你想问
    </div>
    <div className="suggestion-list">
      {SKILL_DATA[skill].map((question, index) => (
        <div
          key={index}
          className="sug-item"
          onClick={() => onSuggestionClick(question)}
        >
          <span className="sug-icon">✨</span>
          <span>{question}</span>
        </div>
      ))}
    </div>
  </>
)

function App() {
  // 用户标识
  const userId = getUserId()
  // 当前选中技能
  const [activeSkill, setActiveSkill] = useState<SkillKey>('activity')
  // 输入框内容
  const [inputValue, setInputValue] = useState('')

  // 使用历史记录 Hook
  const {
    historyList,
    activeHistory,
    setActiveHistory,
    addHistoryItem,
    removeHistoryItem
  } = useHistory({ activeSkill, userId })

  // 欢迎语状态
  const [welcomeText, setWelcomeText] = useState('')
  const [isTyping, setIsTyping] = useState(true)

  // 打字机效果 Key
  const [typingKey, setTypingKey] = useState(0)

  // 建议问题状态（保留用于后续功能）
  const [_suggestedQuestions, setSuggestedQuestions] = useState<string[]>([])

  // 消息完成回调（用于添加历史记录和更新当前会话ID）
  const handleMessageComplete = useCallback((conversationId: string, firstUserMessage: string) => {
    // 更新当前活动的会话ID（支持多轮对话）
    setActiveHistory(conversationId)

    // 使用传入的第一条用户消息作为标题
    if (firstUserMessage) {
      const title = firstUserMessage.slice(0, 20) + (firstUserMessage.length > 20 ? '...' : '')
      addHistoryItem({
        id: conversationId,
        title,
        skill: activeSkill
      })
    }
  }, [activeSkill, addHistoryItem, setActiveHistory])

  // 建议问题加载回调
  const handleSuggestedQuestionsLoad = useCallback((questions: string[]) => {
    setSuggestedQuestions(questions)
  }, [])

  // 使用聊天 Hook
  const {
    messages,
    isLoading,
    sendMessage,
    stopResponse,
    clearMessages,
    setMessages,
    handleLike,
    handleDislike
  } = useChat({
    activeSkill,
    activeHistory,
    userId,
    onMessageComplete: handleMessageComplete,
    onSuggestedQuestionsLoad: handleSuggestedQuestionsLoad
  })

  // 打字机效果
  useEffect(() => {
    let index = 0
    setWelcomeText('')
    setIsTyping(true)

    const timer = setInterval(() => {
      if (index < WELCOME_TEXT.length) {
        setWelcomeText(WELCOME_TEXT.slice(0, index + 1))
        index++
      } else {
        setIsTyping(false)
        clearInterval(timer)
      }
    }, TYPEWRITER_CONFIG.interval)

    return () => clearInterval(timer)
  }, [typingKey])

  // 触发打字机效果
  const triggerTyping = useCallback(() => {
    setTypingKey(prev => prev + 1)
  }, [])

  // 选择技能
  const handleSelectSkill = useCallback((skillKey: SkillKey) => {
    setActiveSkill(skillKey)
    setActiveHistory(null)
    clearMessages()
    triggerTyping()
  }, [setActiveHistory, clearMessages, triggerTyping])

  // 切换历史会话
  const handleSwitchHistory = async (historyItem: HistoryItem) => {
    setActiveHistory(historyItem.id)
    setActiveSkill(historyItem.skill)
    setInputValue('')
    setWelcomeText(WELCOME_TEXT)
    setIsTyping(false)

    try {
      const api = await import('./api')
      const response = await api.getMessageHistory(historyItem.skill, userId, historyItem.id)
      // 兼容两种返回格式：直接数组 或 { data: 数组 }
      const data = Array.isArray(response) ? response : ((response as { data?: ApiMessage[] })?.data || [])

      // 每条记录包含 query 和 answer，需要拆分成两条消息
      const formattedMessages: Message[] = []
      data.forEach((msg: ApiMessage) => {
        // 用户消息
        if (msg.query) {
          formattedMessages.push({
            id: `${msg.id}-user`,
            role: 'user',
            content: msg.query,
            conversationId: msg.conversation_id,
          })
        }
        // AI回复 - 直接传递原始内容，由 MarkdownRenderer 解析
        if (msg.answer) {
          formattedMessages.push({
            id: msg.id,
            role: 'assistant',
            content: msg.answer,
            conversationId: msg.conversation_id,
            liked: msg.feedback?.rating === 'like',
            disliked: msg.feedback?.rating === 'dislike',
          })
        }
      })

      setMessages(formattedMessages)
    } catch (error) {
      console.error('[handleSwitchHistory error]', error)
      message.error('获取历史消息失败')
    }
  }

  // 开启新对话
  const handleStartNewChat = useCallback(() => {
    setActiveHistory(null)
    setInputValue('')
    // 保持当前技能，不重置为 activity
    clearMessages()
    triggerTyping()
  }, [setActiveHistory, clearMessages, triggerTyping])

  // 发送消息
  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim()) return
    const content = inputValue
    setInputValue('') // 立即清空输入框
    await sendMessage(content)
  }, [inputValue, sendMessage])

  // 点击推荐问题 - 直接发送
  const handleSuggestionClick = useCallback((question: string) => {
    if (question.trim()) {
      sendMessage(question)
    }
  }, [sendMessage])

  // 检查输入状态
  const hasInput = inputValue.trim().length > 0

  // 键盘事件
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
      e.preventDefault()
      if (inputValue.trim()) {
        const content = inputValue
        setInputValue('')
        sendMessage(content)
      }
    }
  }, [inputValue, isLoading, sendMessage])

  // 删除历史会话
  const handleDeleteHistory = useCallback(async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    await removeHistoryItem(id)
    
    if (activeHistory === id) {
      setActiveHistory(null)
      clearMessages()
      triggerTyping()
    }
  }, [activeHistory, setActiveHistory, clearMessages, removeHistoryItem, triggerTyping])

  // 输入变化处理
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  return (
    <div className="app">
      {/* 左侧边栏 */}
      <Sidebar
        activeSkill={activeSkill}
        activeHistory={activeHistory}
        skillCards={skillCards}
        historyList={historyList}
        onSelectSkill={handleSelectSkill}
        onSwitchHistory={handleSwitchHistory}
        onDeleteHistory={handleDeleteHistory}
      />

      {/* 主内容区 */}
      <main className="main-content">
        <div className="content-wrapper">
          {messages.length === 0 && (
            <div className="welcome-row">
              <div className="welcome-avatar"></div>
              <div className={`welcome-text ${isTyping ? 'typing' : ''}`}>
                {welcomeText}
              </div>
            </div>
          )}

          {/* 消息列表 */}
          {messages.length > 0 && (
            <MessageList
              messages={messages}
              activeSkill={activeSkill}
              onLike={handleLike}
              onDislike={handleDislike}
            />
          )}

          {/* 推荐问题或操作说明 - 无消息时显示 */}
          {messages.length === 0 && activeSkill === 'activity' && (
            <SuggestionList skill="activity" onSuggestionClick={handleSuggestionClick} />
          )}

          {messages.length === 0 && activeSkill === 'system' && (
            <SuggestionList skill="system" onSuggestionClick={handleSuggestionClick} />
          )}

          {messages.length === 0 && activeSkill === 'learning' && (
            <div className="guide-section">
              <div className="guide-description">
                请在对话框输入您关注的学段、学科、课例主题关键字，我来为您推荐往届优秀课例
              </div>
            </div>
          )}

          {messages.length === 0 && activeSkill === 'growth' && (
            <div className="guide-section">
              <div className="guide-description">
                新老师们，你在教学上遇到过哪些困惑？学生参与度不高、课堂纪律难管、和家长沟通不顺…..聊聊你正在面对的教学挑战，我来为你抽取对应的成长问题和锦囊妙招，前辈教师的实战经验或许正是你需要的答案！
              </div>
            </div>
          )}
        </div>

        {/* 底部固定输入区 */}
        <InputArea
          inputValue={inputValue}
          hasInput={hasInput}
          isLoading={isLoading}
          onInputChange={handleInputChange}
          onSend={handleSendMessage}
          onStop={stopResponse}
          onNewChat={handleStartNewChat}
          onKeyDown={handleKeyDown}
        />
      </main>

      {/* 右侧技能详情面板 */}
      <RightPanel
        activeSkill={activeSkill}
        activeHistory={activeHistory}
        historyList={historyList}
      />
    </div>
  )
}

export default App
